import { useState } from 'react';
import { FincurryLogo } from '@/components/brand/fincurry-logo';
import { apiBaseUrl, APP_ONELINK_URL } from '@/lib/public-config';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ResetPasswordClient({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const isMissingToken = !token || token.length === 0;

  async function onSubmit() {
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must include at least one letter and one number.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setStatus('submitting');
    try {
      const res = await fetch(`${apiBaseUrl()}/auth/reset-password`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json();
      if (!res.ok || body.error) {
        setStatus('error');
        setError(mapResetError(body?.error?.code, body?.error?.message));
        return;
      }
      setStatus('success');
    } catch {
      setStatus('error');
      setError('Network error. Try again.');
    }
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-[-30%] left-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-500/[0.03] blur-[120px]" />
      <div className="absolute bottom-[-20%] right-[-5%] w-[400px] h-[400px] rounded-full bg-emerald-500/[0.02] blur-[100px]" />

      <div className="relative w-full max-w-[400px]">
        <div className="flex justify-center mb-6">
          <FincurryLogo size="xl" />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2 text-center">
          Reset your password
        </h1>
        <p className="text-zinc-400 text-sm text-center mb-8">
          Choose a new password for your Fincurry account.
        </p>

        {isMissingToken ? (
          <div className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 text-center">
            Invalid or missing reset token. Request a new reset link from the app.
          </div>
        ) : status === 'success' ? (
          <SuccessBlock />
        ) : (
          <div className="space-y-4">
            <PasswordInput
              label="New password"
              value={password}
              onChange={setPassword}
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              placeholder="At least 8 characters"
              disabled={status === 'submitting'}
              autoComplete="new-password"
              onEnterSubmit={onSubmit}
            />

            <PasswordInput
              label="Confirm password"
              value={confirm}
              onChange={setConfirm}
              show={showConfirm}
              onToggleShow={() => setShowConfirm((v) => !v)}
              placeholder="Retype your new password"
              disabled={status === 'submitting'}
              autoComplete="new-password"
              onEnterSubmit={onSubmit}
            />

            {error && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={onSubmit}
              disabled={status === 'submitting'}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold rounded-lg transition-colors"
            >
              {status === 'submitting' ? 'Updating…' : 'Set new password'}
            </button>
          </div>
        )}

        <p className="text-zinc-600 text-xs text-center mt-8">
          Link expires 1 hour after request · single-use for security
        </p>
      </div>
    </div>
  );
}

interface PasswordInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
  placeholder: string;
  disabled: boolean;
  autoComplete: string;
  onEnterSubmit?: () => void;
}

function PasswordInput({
  label,
  value,
  onChange,
  show,
  onToggleShow,
  placeholder,
  disabled,
  autoComplete,
  onEnterSubmit,
}: PasswordInputProps) {
  return (
    <div>
      <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wide">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onEnterSubmit) {
              e.preventDefault();
              onEnterSubmit();
            }
          }}
          className="w-full px-4 py-3 pr-12 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={onToggleShow}
          disabled={disabled}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-zinc-500 hover:text-zinc-300 disabled:opacity-40"
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// AppsFlyer OneLink — opens the app if installed on either iOS or
// Android, otherwise redirects to the matching store. deep_link_value
// hints the app to route to the login screen once open.
//
// Per-env AppsFlyer accounts (dev / staging / prod each own a
// distinct OneLink template) are configured via
// NEXT_PUBLIC_APP_ONELINK_URL. The fallback is the prod template so
// a misconfigured deploy still routes users somewhere useful.
const APP_OPEN_URL = APP_ONELINK_URL;

function SuccessBlock() {
  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm p-4">
        <div className="flex items-center gap-2 font-semibold text-emerald-200 mb-1">
          <CheckIcon />
          Password updated
        </div>
        <p className="text-emerald-300/90 text-xs leading-relaxed">
          Open Fincurry and log in with your new password.
        </p>
      </div>

      <a
        href={APP_OPEN_URL}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold rounded-lg transition-colors"
      >
        <PlayIcon />
        Open Fincurry app
      </a>

      <p className="text-zinc-500 text-xs text-center">
        Don&apos;t have the app? The button will take you to the store.
      </p>
    </div>
  );
}

function mapResetError(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'TOKEN_ALREADY_USED':
      return 'This reset link has already been used. Request a new one from the app.';
    case 'TOKEN_EXPIRED':
      return 'This reset link has expired. Request a new one from the app.';
    case 'INVALID_TOKEN':
      return 'This reset link is invalid. Request a new one from the app.';
    default:
      return fallback || 'Reset failed. Try again.';
  }
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 20.5V3.5a1 1 0 0 1 1.53-.85l14.06 8.5a1 1 0 0 1 0 1.7l-14.06 8.5A1 1 0 0 1 3 20.5z" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.71 19.71 0 0 1 4.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.72 19.72 0 0 1-3.17 4.32M14.12 14.12A3 3 0 1 1 9.88 9.88" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
